import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Users, 
  MessageSquare, 
  Send, 
  CheckCircle, 
  AlertCircle, 
  RefreshCw,
  UserPlus,
  Webhook,
  Clock,
  Target,
  Download,
  Loader2,
  Calendar,
  Plus,
  Minus,
  CheckSquare,
  Square,
  Globe,
  Bell,
  Settings as SettingsIcon
} from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface SlackEmployee {
  id: number;
  slackId: string;
  name: string;
  email: string | null;
  username: string | null;
  displayName: string | null;
  realName: string | null;
  isActive: boolean;
  isBot: boolean;
  profileImage: string | null;
  timezone: string | null;
  status: string;
  isNewJoiner: boolean;
  createdAt: string;
  updatedAt: string;
  trainingStatus: {
    isCompleted: boolean;
    moduleCount: number;
    completedCount: number;
  };
}

interface SlackFetch {
  id: number;
  fetchType: string;
  totalEmployees: number;
  newEmployees: number;
  webhookUrl: string | null;
  status: string;
  errorMessage: string | null;
  fetchedAt: string;
}

export default function SlackIntegration() {
  const { toast } = useToast();
  const [messageContent, setMessageContent] = useState("Hi! We noticed you haven't completed your learning modules yet. Please complete them to finish your onboarding process. Thanks!");
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [selectedNewJoiners, setSelectedNewJoiners] = useState<string[]>([]);
  const [bulkMessageContent, setBulkMessageContent] = useState("Hi! We noticed you haven't completed your learning modules yet. Please complete them to finish your onboarding process. Thanks!");
  const [scheduledDateTime, setScheduledDateTime] = useState<string>("");
  const [selectedTimezone, setSelectedTimezone] = useState<string>('Asia/Kolkata');
  const [showSchedulingModal, setShowSchedulingModal] = useState(false);
  const [schedulingModalContext, setSchedulingModalContext] = useState<'employees' | 'newJoiners'>('employees');
  const [isAutoFetching, setIsAutoFetching] = useState(false);
  
  // Automated Reminder Settings state
  const [automatedReminderEnabled, setAutomatedReminderEnabled] = useState(false);
  const [reminderTime, setReminderTime] = useState("09:00");
  const [reminderTimezone, setReminderTimezone] = useState("Asia/Kolkata");
  const [reminderMessage, setReminderMessage] = useState("Hi! We noticed you haven't completed your learning modules yet. Please complete them to finish your onboarding process. Thanks!");
  const [fetchProgress, setFetchProgress] = useState(0);
  const [hasAutoFetched, setHasAutoFetched] = useState(false);
  

  // Fetch all Slack employees - POLLING DISABLED to prevent rate limiting
  const { data: employeesData, isLoading: loadingEmployees, refetch: refetchEmployees } = useQuery({
    queryKey: ['/slack/employees'],
    retry: false,
    refetchInterval: false, // Polling completely disabled
    refetchOnWindowFocus: false, // Disable refetch on window focus
    refetchOnMount: true, // Only fetch on mount
  });

  // Fetch new joiners - POLLING DISABLED to prevent rate limiting
  const { data: newJoinersData, isLoading: loadingNewJoiners, refetch: refetchNewJoiners } = useQuery({
    queryKey: ['/slack/new-joiners'],
    retry: false,
    refetchInterval: false, // Polling completely disabled
    refetchOnWindowFocus: false, // Disable refetch on window focus
    refetchOnMount: true, // Only fetch on mount
    staleTime: 0, // Override global Infinity to allow refetching
  });

  const employees: SlackEmployee[] = (employeesData as any)?.employees || [];
  const newJoiners: SlackEmployee[] = (newJoinersData as any)?.newJoiners || [];
  

  // Fetch automated reminder settings
  const { data: automatedReminderData, refetch: refetchAutomatedReminder } = useQuery({
    queryKey: ['/automated-reminders'],
    retry: false,
    refetchInterval: false,
  });

  // Load settings into state when fetched
  useEffect(() => {
    if ((automatedReminderData as any)?.settings) {
      const settings = (automatedReminderData as any).settings;
      setAutomatedReminderEnabled(settings.isEnabled || false);
      setReminderTime(settings.reminderTime || "09:00");
      setReminderTimezone(settings.timezone || "Asia/Kolkata");
      setReminderMessage(settings.messageContent || "Hi! We noticed you haven't completed your learning modules yet. Please complete them to finish your onboarding process. Thanks!");
    }
  }, [automatedReminderData]);

  // AUTO-FETCH COMPLETELY DISABLED to prevent rate limiting issues
  useEffect(() => {
    // Clear all problematic state that could trigger requests
    localStorage.removeItem('slackAutoFetchDone');
    sessionStorage.removeItem('slackAutoFetchCompleted');
    
    // Reset any stuck auto-fetch state
    setIsAutoFetching(false);
    setFetchProgress(0);
    setHasAutoFetched(false);
    
    // Show notification only once per session
    const hasShownNotification = sessionStorage.getItem('manualRefreshNotificationShown');
    if (!hasShownNotification) {
      sessionStorage.setItem('manualRefreshNotificationShown', 'true');
      toast({
        title: "Manual Refresh Required",
        description: "Auto-fetch is temporarily disabled. Please use the refresh button to fetch employees manually.",
      });
    }
    
    console.log('Auto-fetch disabled - use manual refresh button instead');
  }, []);

  // Monitor completion when employees are fetched during auto-fetch
  useEffect(() => {
    if (isAutoFetching && employees.length > 50) { // Use 50 as a reasonable threshold for 480 employees
      setIsAutoFetching(false);
      setFetchProgress(100);
      toast({
        title: "Success!",
        description: `Successfully loaded ${employees.length} employees from Slack workspace!`
      });
    }
  }, [employees.length, isAutoFetching]);

  // Trigger Slack fetch mutation (webhook URL is configured in backend secrets)
  const fetchSlackDataMutation = useMutation({
    mutationFn: () => 
      apiRequest('POST', '/slack/fetch', {}),
    onSuccess: async (response) => {
      const data = await response.json();
      
      if (isAutoFetching) {
        toast({
          title: "Processing",
          description: `Webhook initiated successfully! Processing ${data.totalEmployees || '480'} employees...`
        });
        // React Query polling will handle the auto-refreshing
      } else {
        // Manual fetch
        toast({
          title: "Success",
          description: `Slack employee fetch initiated! Status: ${data.status}`
        });
        
        setTimeout(() => {
          refetchEmployees();
          refetchNewJoiners();
        }, 2000);
      }
    },
    onError: (error: any) => {
      setIsAutoFetching(false);
      setFetchProgress(0);
      toast({
        title: "Error",
        description: error.message || "Failed to initiate Slack fetch",
        variant: "destructive"
      });
    }
  });

  // Send individual message mutation
  const sendMessageMutation = useMutation({
    mutationFn: (data: { slackEmployeeId: number; messageContent: string; messageType?: string }) => 
      apiRequest('POST', '/slack/message', { ...data, messageType: data.messageType || "personal" }),
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Message sent successfully"
      });
      setMessageContent("");
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to send message",
        variant: "destructive"
      });
    }
  });

  // Legacy bulk message mutation removed - using consolidated bulkMessageMutation instead

  // Mark employees as not new mutation
  const markNotNewMutation = useMutation({
    mutationFn: (slackIds: string[]) => 
      apiRequest('POST', '/slack/employees/mark-not-new', { slackIds }),
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Employees marked as no longer new joiners"
      });
      refetchEmployees();
      refetchNewJoiners();
      setSelectedEmployees([]);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update employee status",
        variant: "destructive"
      });
    }
  });

  // Fixed bulk messaging mutation with scheduling support
  const bulkMessageMutation = useMutation({
    mutationFn: async (data: { recipientIds: number[]; messageContent: string; messageType?: string; scheduledAt?: string; timezone?: string }) => {
      const response = await apiRequest('POST', '/slack/bulk-message', data);
      return await response.json();
    },
    onSuccess: (data) => {
      if (data.scheduled) {
        toast({
          title: "Success", 
          description: `Bulk message scheduled for ${data.scheduledMessage.totalRecipients} employees`
        });
      } else {
        toast({
          title: "Success",
          description: `Bulk message sent: ${data.sentCount} sent, ${data.failedCount} failed`
        });
      }
      setSelectedEmployees([]);
      setSelectedNewJoiners([]);
      setShowSchedulingModal(false);
      setBulkMessageContent("Hi! We noticed you haven't completed your learning modules yet. Please complete them to finish your onboarding process. Thanks!");
      setScheduledDateTime("");
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to send bulk message",
        variant: "destructive"
      });
    }
  });

  const handleFetchSlackData = () => {
    fetchSlackDataMutation.mutate();
  };

  const handleSendMessage = (employeeId: number) => {
    // Use default reminder message for individual sends if messageContent is empty
    const defaultReminderMessage = "Hi! We noticed you haven't completed your learning modules yet. Please complete them to finish your onboarding process. Thanks!";
    const finalMessage = messageContent.trim() || defaultReminderMessage;
    
    sendMessageMutation.mutate({ slackEmployeeId: employeeId, messageContent: finalMessage });
  };

  // Legacy bulk message handler removed - using handleBulkMessage instead

  const handleMarkNotNew = () => {
    if (selectedEmployees.length === 0) {
      toast({
        title: "Error",
        description: "Please select employees to mark as not new",
        variant: "destructive"
      });
      return;
    }
    markNotNewMutation.mutate(selectedEmployees);
  };

  // Handler for saving automated reminder settings
  const handleSaveAutomatedReminderSettings = async () => {
    if (!reminderMessage.trim()) {
      toast({
        title: "Error",
        description: "Please enter a reminder message",
        variant: "destructive"
      });
      return;
    }
    
    try {
      const data = {
        isEnabled: automatedReminderEnabled,
        reminderTime: reminderTime,
        timezone: reminderTimezone,
        messageContent: reminderMessage.trim()
      };
      
      const endpoint = (automatedReminderData as any)?.settings?.id 
        ? `/automated-reminders/${(automatedReminderData as any)?.settings?.id}`
        : '/automated-reminders';
      
      const method = (automatedReminderData as any)?.settings?.id ? 'PUT' : 'POST';
      
      await apiRequest(method, endpoint, data);
      
      toast({
        title: "Success",
        description: "Automated reminder settings saved successfully!"
      });
      refetchAutomatedReminder();
      
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to save automated reminder settings"
      });
    }
  };

  const toggleEmployeeSelection = (slackId: string) => {
    setSelectedEmployees(prev => 
      prev.includes(slackId) 
        ? prev.filter(id => id !== slackId)
        : [...prev, slackId]
    );
  };

  const toggleNewJoinerSelection = (slackId: string) => {
    setSelectedNewJoiners(prev => 
      prev.includes(slackId) 
        ? prev.filter(id => id !== slackId)
        : [...prev, slackId]
    );
  };

  const selectAllEmployees = () => {
    if (employees) {
      setSelectedEmployees(employees.map(emp => emp.slackId));
    }
  };

  const clearAllEmployees = () => {
    setSelectedEmployees([]);
  };

  const selectAllNewJoiners = () => {
    if (newJoiners) {
      setSelectedNewJoiners(newJoiners.map(emp => emp.slackId));
    }
  };

  const clearAllNewJoiners = () => {
    setSelectedNewJoiners([]);
  };

  const handleBulkMessage = (isScheduled: boolean = false) => {
    const allSelectedIds = [...selectedEmployees, ...selectedNewJoiners];
    if (allSelectedIds.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one employee",
        variant: "destructive"
      });
      return;
    }

    if (!bulkMessageContent.trim()) {
      toast({
        title: "Error", 
        description: "Please enter a message",
        variant: "destructive"
      });
      return;
    }

    if (isScheduled) {
      if (!scheduledDateTime) {
        toast({
          title: "Error",
          description: "Please select a scheduled date and time",
          variant: "destructive"
        });
        return;
      }
      
      // Find employee IDs from slackIds - proper mapping implementation
      const employeeIds: number[] = [];
      const seenIds = new Set<number>(); // Prevent duplicates
      
      employees?.forEach(emp => {
        if (selectedEmployees.includes(emp.slackId) && !seenIds.has(emp.id)) {
          employeeIds.push(emp.id);
          seenIds.add(emp.id);
        }
      });
      
      newJoiners?.forEach(emp => {
        if (selectedNewJoiners.includes(emp.slackId) && !seenIds.has(emp.id)) {
          employeeIds.push(emp.id);
          seenIds.add(emp.id);
        }
      });

      bulkMessageMutation.mutate({
        recipientIds: employeeIds,
        messageContent: bulkMessageContent.trim(),
        messageType: "bulk",
        scheduledAt: scheduledDateTime,
        timezone: selectedTimezone
      });
    } else {
      // Send immediately - proper mapping implementation
      const employeeIds: number[] = [];
      const seenIds = new Set<number>(); // Prevent duplicates
      
      employees?.forEach(emp => {
        if (selectedEmployees.includes(emp.slackId) && !seenIds.has(emp.id)) {
          employeeIds.push(emp.id);
          seenIds.add(emp.id);
        }
      });
      
      newJoiners?.forEach(emp => {
        if (selectedNewJoiners.includes(emp.slackId) && !seenIds.has(emp.id)) {
          employeeIds.push(emp.id);
          seenIds.add(emp.id);
        }
      });

      bulkMessageMutation.mutate({
        recipientIds: employeeIds,
        messageContent: bulkMessageContent.trim(),
        messageType: "bulk"
      });
    }
  };

  return (
    <div className="min-h-screen relative" style={{ background: 'var(--gradient-subtle)' }}>
      {/* Modern background decoration */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(65,0,153,0.08)_1px,transparent_0)] [background-size:40px_40px]"></div>
      <div className="absolute inset-0" style={{ background: 'var(--gradient-glass)', opacity: 0.6 }}></div>
      
      <div className="p-8 space-y-8 relative z-10">
        <div className="flex items-start justify-between animate-slide-up">
          <div className="space-y-3 flex-1 pr-8">
            <h1 className="text-modern-lg text-gradient font-extrabold tracking-tight" data-testid="title-slack-integration">
              Slack Integration
            </h1>
            <p className="text-servify-medium-grey text-lg leading-relaxed">
              Manage employee onboarding and training module completion tracking via Slack with powerful automation
            </p>
          </div>
        <div className="flex gap-2 flex-shrink-0">
          <Button 
            onClick={() => fetchSlackDataMutation.mutate()}
            variant="outline"
            className="flex items-center gap-2"
            data-testid="button-refresh"
            disabled={fetchSlackDataMutation.isPending || isAutoFetching}
          >
            {fetchSlackDataMutation.isPending ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                Fetching...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4" />
                Refresh Data
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Auto-fetch Progress Indicator */}
      {isAutoFetching && (
        <Card className="card-glass border-l-4 border-l-servify-purple shadow-glow animate-scale-in">
          <CardContent className="p-8">
            <div className="flex items-center gap-6">
              <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-servify-purple to-servify-purple-light rounded-2xl shadow-lg">
                <Download className="h-8 w-8 text-white animate-pulse" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-servify-dark-grey mb-2">
                  Fetching 480 Employees from Slack Workspace
                </h3>
                <p className="text-sm text-servify-medium-grey mb-3">
                  Processing employee data from your Slack workspace. This process takes approximately 2:30 minutes.
                </p>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-servify-medium-grey">Progress</span>
                    <span className="text-servify-purple font-medium">{Math.round(fetchProgress)}%</span>
                  </div>
                  <Progress value={fetchProgress} className="h-2" />
                </div>
                <div className="flex items-center gap-2 mt-3 text-sm text-servify-medium-grey">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Currently syncing from n8n webhook...
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="new-joiners" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="new-joiners" data-testid="tab-new-joiners">New Joiners</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
        </TabsList>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-8">
          <Card data-testid="card-automated-reminders" className="card-glass shadow-float animate-fade-in">
            <CardHeader className="pb-6">
              <CardTitle className="flex items-center gap-3 text-modern-md">
                <div className="p-2 bg-gradient-to-br from-servify-purple to-servify-purple-dark rounded-lg">
                  <Bell className="h-6 w-6 text-white" />
                </div>
                Automated Daily Reminders
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable/Disable Switch */}
              <div className="flex items-center justify-between p-4 rounded-lg border border-gray-200">
                <div className="flex items-center gap-3">
                  <SettingsIcon className="h-5 w-5 text-servify-purple" />
                  <div>
                    <h4 className="font-medium text-servify-dark-grey">Enable Automated Reminders</h4>
                    <p className="text-sm text-servify-medium-grey">Send daily reminders to new joiners with incomplete training</p>
                  </div>
                </div>
                <Checkbox 
                  checked={automatedReminderEnabled}
                  onCheckedChange={(checked) => setAutomatedReminderEnabled(Boolean(checked))}
                  data-testid="checkbox-enable-automated-reminders"
                />
              </div>

              {automatedReminderEnabled && (
                <>
                  {/* Reminder Time */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="reminder-time">Reminder Time</Label>
                      <Input
                        id="reminder-time"
                        type="time"
                        value={reminderTime}
                        onChange={(e) => {
                          console.log('Time changed to:', e.target.value);
                          setReminderTime(e.target.value);
                        }}
                        className="input-modern"
                        data-testid="input-reminder-time"
                        step="60"
                        min="00:00"
                        max="23:59"
                        placeholder="HH:MM"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="reminder-timezone">Timezone</Label>
                      <select
                        id="reminder-timezone"
                        value={reminderTimezone}
                        onChange={(e) => setReminderTimezone(e.target.value)}
                        className="w-full h-10 px-3 py-2 border border-input rounded-md bg-background text-sm"
                        data-testid="select-reminder-timezone"
                      >
                        <option value="Asia/Kolkata">Indian Standard Time (IST)</option>
                        <option value="UTC">UTC</option>
                        <option value="America/New_York">Eastern Time (ET)</option>
                        <option value="America/Los_Angeles">Pacific Time (PT)</option>
                        <option value="Europe/London">Greenwich Mean Time (GMT)</option>
                        <option value="Asia/Tokyo">Japan Standard Time (JST)</option>
                      </select>
                    </div>
                  </div>

                  {/* Reminder Message */}
                  <div className="space-y-2">
                    <Label htmlFor="reminder-message">Reminder Message</Label>
                    <Textarea
                      id="reminder-message"
                      placeholder="Hi! We noticed you haven't completed your learning modules yet. Please complete them to finish your onboarding process. Thanks!"
                      value={reminderMessage}
                      onChange={(e) => setReminderMessage(e.target.value)}
                      rows={4}
                      className="min-h-[100px]"
                      data-testid="textarea-reminder-message"
                    />
                    <p className="text-sm text-servify-medium-grey">
                      This message will be sent daily at {reminderTime} ({reminderTimezone}) to new joiners with incomplete training modules
                    </p>
                  </div>

                  {/* Save Button */}
                  <Button 
                    onClick={handleSaveAutomatedReminderSettings}
                    className="w-full bg-servify-purple hover:bg-servify-purple/90 text-white"
                    data-testid="button-save-automated-reminders"
                  >
                    <SettingsIcon className="h-4 w-4 mr-2 text-white" />
                    Save Automated Reminder Settings
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>


        {/* New Joiners Tab */}
        <TabsContent value="new-joiners" className="space-y-6">
          {/* Bulk Selection Controls for New Joiners */}
          <Card data-testid="card-bulk-controls-new-joiners">
            <CardContent className="p-4">
              <div className="flex justify-between items-center mb-4">
                <div className="text-sm text-servify-medium-grey">
                  {selectedNewJoiners.length} of {newJoiners.length} new joiners selected
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={selectAllNewJoiners}
                    disabled={selectedNewJoiners.length === newJoiners.length}
                    data-testid="button-select-all-new-joiners"
                  >
                    <CheckSquare className="h-4 w-4 mr-1" />
                    Select All
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={clearAllNewJoiners}
                    disabled={selectedNewJoiners.length === 0}
                    data-testid="button-clear-all-new-joiners"
                  >
                    <Square className="h-4 w-4 mr-1" />
                    Clear All
                  </Button>
                </div>
              </div>
              
              {selectedNewJoiners.length > 0 && (
                <div className="space-y-4">
                  <Textarea
                    placeholder="Enter your message for selected new joiners..."
                    value={bulkMessageContent}
                    onChange={(e) => setBulkMessageContent(e.target.value)}
                    className="min-h-[100px]"
                    data-testid="textarea-bulk-message-new-joiners"
                  />
                  <div className="flex gap-3">
                    <Button 
                      onClick={() => handleBulkMessage(false)}
                      disabled={!bulkMessageContent.trim() || bulkMessageMutation.isPending || selectedNewJoiners.length === 0}
                      className="bg-servify-purple hover:bg-servify-purple/90 text-white"
                      data-testid="button-send-bulk-new-joiners"
                    >
                      {bulkMessageMutation.isPending ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4 mr-2" />
                      )}
                      Send Now ({selectedNewJoiners.length} recipients)
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => {
                        setSchedulingModalContext('newJoiners');
                        setShowSchedulingModal(true);
                      }}
                      disabled={!bulkMessageContent.trim() || selectedNewJoiners.length === 0}
                      data-testid="button-schedule-bulk-new-joiners"
                    >
                      <Clock className="h-4 w-4 mr-2" />
                      Schedule ({selectedNewJoiners.length} recipients)
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-servify-dark-grey">
              New Joiners ({newJoiners.length})
            </h2>
            {selectedEmployees.length > 0 && (
              <Button
                onClick={handleMarkNotNew}
                disabled={markNotNewMutation.isPending}
                variant="outline"
                data-testid="button-mark-not-new"
              >
                <Target className="h-4 w-4 mr-2" />
                Mark as Not New ({selectedEmployees.length})
              </Button>
            )}
          </div>

          {loadingNewJoiners ? (
            <div className="text-center py-8">Loading new joiners...</div>
          ) : newJoiners.length === 0 ? (
            <Alert data-testid="alert-no-new-joiners">
              <UserPlus className="h-4 w-4" />
              <AlertDescription>
                No new joiners found. Run a Slack fetch to identify new employees.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {newJoiners.map((employee) => (
                <Card 
                  key={employee.id} 
                  className={`card-modern hover:shadow-green-glow border-servify-green/30 transition-all duration-300 ${selectedEmployees.includes(employee.slackId) ? 'bg-gradient-to-br from-servify-green/5 to-servify-green/10 shadow-green-glow' : 'hover:border-servify-green/50'}`}
                  data-testid={`card-new-joiner-${employee.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-3 relative overflow-hidden">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <Checkbox
                          checked={selectedNewJoiners.includes(employee.slackId)}
                          onCheckedChange={() => toggleNewJoinerSelection(employee.slackId)}
                          data-testid={`checkbox-new-joiner-${employee.id}`}
                        />
                        {employee.profileImage ? (
                          <img 
                            src={employee.profileImage} 
                            alt={employee.name}
                            className="w-10 h-10 rounded-full flex-shrink-0"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-servify-green/10 flex items-center justify-center flex-shrink-0">
                            <Users className="h-5 w-5 text-servify-green" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <h3 className="font-medium text-servify-dark-grey truncate">{employee.name}</h3>
                          <p className="text-sm text-servify-medium-grey truncate">{employee.email}</p>
                        </div>
                      </div>
                      <Badge variant="secondary" className="bg-servify-green/10 text-servify-green ml-2 flex-shrink-0">
                        New
                      </Badge>
                    </div>
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-servify-medium-grey">Training Progress:</span>
                        <span className={employee.trainingStatus.isCompleted ? 'text-servify-green' : 'text-orange-500'}>
                          {employee.trainingStatus.completedCount}/{employee.trainingStatus.moduleCount}
                        </span>
                      </div>
                      
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${employee.trainingStatus.isCompleted ? 'bg-servify-green' : 'bg-orange-500'}`}
                          style={{ 
                            width: `${employee.trainingStatus.moduleCount > 0 ? (employee.trainingStatus.completedCount / employee.trainingStatus.moduleCount) * 100 : 0}%` 
                          }}
                        />
                      </div>
                    </div>

                    <Button
                      variant={employee.trainingStatus.isCompleted ? "secondary" : "default"}
                      size="sm"
                      className="w-full"
                      onClick={() => handleSendMessage(employee.id)}
                      disabled={sendMessageMutation.isPending}
                      data-testid={`button-message-new-${employee.id}`}
                    >
                      {employee.trainingStatus.isCompleted ? (
                        <>
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Done
                        </>
                      ) : (
                        <>
                          <MessageSquare className="h-4 w-4 mr-2" />
                          Send Reminder
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Schedule Message Popup Modal */}
      <Dialog open={showSchedulingModal} onOpenChange={setShowSchedulingModal}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto bg-white border-2 border-servify-light-grey shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-servify-dark-grey flex items-center gap-2">
              <Calendar className="h-5 w-5 text-servify-purple" />
              Schedule Message
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Recipients Count */}
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-servify-purple/10 rounded-full">
                <Users className="h-4 w-4 text-servify-purple" />
                <span className="text-sm font-medium text-servify-purple">
                  {schedulingModalContext === 'employees' ? selectedEmployees.length : selectedNewJoiners.length} Recipients Selected
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Time and Date Selection */}
              <div className="space-y-4">
                {/* Time Picker */}
                <div className="bg-gradient-to-br from-servify-purple/5 to-servify-green/5 rounded-lg p-4 border border-servify-purple/10">
                  <Label className="text-sm font-medium text-servify-dark-grey mb-3 block flex items-center gap-2">
                    <Clock className="h-4 w-4 text-servify-purple" />
                    Select Time
                  </Label>
                  <div className="flex items-center justify-center gap-4">
                    <div className="flex flex-col items-center">
                      <label className="text-xs text-servify-medium-grey mb-2 font-medium">Hour</label>
                      <select
                        value={scheduledDateTime ? new Date(scheduledDateTime + ':00').getHours().toString().padStart(2, '0') : '09'}
                        onChange={(e) => {
                          const hours = parseInt(e.target.value);
                          let targetDate;
                          
                          if (scheduledDateTime) {
                            // If we already have a scheduled date, preserve the date and minutes
                            targetDate = new Date(scheduledDateTime + ':00'); // Add seconds to make it a full ISO string
                            targetDate.setHours(hours);
                          } else {
                            // If no scheduled date yet, create new with today's date, selected hour, and 00 minutes
                            targetDate = new Date();
                            targetDate.setHours(hours, 0, 0, 0);
                          }
                          
                          // Format as local datetime string (YYYY-MM-DDTHH:mm)
                          const year = targetDate.getFullYear();
                          const month = (targetDate.getMonth() + 1).toString().padStart(2, '0');
                          const day = targetDate.getDate().toString().padStart(2, '0');
                          const hour = targetDate.getHours().toString().padStart(2, '0');
                          const minute = targetDate.getMinutes().toString().padStart(2, '0');
                          
                          setScheduledDateTime(`${year}-${month}-${day}T${hour}:${minute}`);
                        }}
                        className="bg-white border-2 border-servify-purple/20 rounded-lg px-4 py-3 text-lg font-semibold text-center w-20 text-servify-dark-grey focus:ring-2 focus:ring-servify-purple focus:border-servify-purple shadow-sm hover:border-servify-purple/40 transition-colors"
                        data-testid="select-hour"
                      >
                        {Array.from({ length: 24 }, (_, i) => (
                          <option key={i} value={i.toString().padStart(2, '0')}>
                            {i.toString().padStart(2, '0')}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="text-2xl font-bold text-servify-purple mt-6">:</div>
                    <div className="flex flex-col items-center">
                      <label className="text-xs text-servify-medium-grey mb-2 font-medium">Minute</label>
                      <select
                        value={scheduledDateTime ? new Date(scheduledDateTime + ':00').getMinutes().toString().padStart(2, '0') : '00'}
                        onChange={(e) => {
                          const minutes = parseInt(e.target.value);
                          let targetDate;
                          
                          if (scheduledDateTime) {
                            // If we already have a scheduled date, preserve the date and hour
                            targetDate = new Date(scheduledDateTime + ':00'); // Add seconds to make it a full ISO string
                            targetDate.setMinutes(minutes);
                          } else {
                            // If no scheduled date yet, create new with today's date, default hour (9 AM), and selected minutes
                            targetDate = new Date();
                            targetDate.setHours(9, minutes, 0, 0);
                          }
                          
                          // Format as local datetime string (YYYY-MM-DDTHH:mm)
                          const year = targetDate.getFullYear();
                          const month = (targetDate.getMonth() + 1).toString().padStart(2, '0');
                          const day = targetDate.getDate().toString().padStart(2, '0');
                          const hour = targetDate.getHours().toString().padStart(2, '0');
                          const minute = targetDate.getMinutes().toString().padStart(2, '0');
                          
                          setScheduledDateTime(`${year}-${month}-${day}T${hour}:${minute}`);
                        }}
                        className="bg-white border-2 border-servify-purple/20 rounded-lg px-4 py-3 text-lg font-semibold text-center w-20 text-servify-dark-grey focus:ring-2 focus:ring-servify-purple focus:border-servify-purple shadow-sm hover:border-servify-purple/40 transition-colors"
                        data-testid="select-minute"
                      >
                        {Array.from({ length: 12 }, (_, i) => {
                          const minute = i * 5;
                          return (
                            <option key={minute} value={minute.toString().padStart(2, '0')}>
                              {minute.toString().padStart(2, '0')}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>
                  <div className="text-center mt-3">
                    <span className="text-xs text-servify-medium-grey">Choose your preferred time for message delivery</span>
                  </div>
                </div>

                {/* Date Picker */}
                <div className="bg-gradient-to-br from-servify-green/5 to-servify-purple/5 rounded-lg p-4 border border-servify-green/10">
                  <Label className="text-sm font-medium text-servify-dark-grey mb-3 block flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-servify-green" />
                    Select Date
                  </Label>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      {['Today', 'Tomorrow'].map((option) => {
                        const today = new Date();
                        const tomorrow = new Date(today);
                        tomorrow.setDate(tomorrow.getDate() + 1);
                        
                        const date = option === 'Today' ? today : tomorrow;
                        const isSelected = scheduledDateTime && 
                          new Date(scheduledDateTime + ':00').toDateString() === date.toDateString();

                        return (
                          <button
                            key={option}
                            onClick={() => {
                              let hours = 9; // Default hour
                              let minutes = 0; // Default minutes
                              
                              // If user has already set a time, preserve it exactly
                              if (scheduledDateTime) {
                                const currentTime = new Date(scheduledDateTime + ':00'); // Add seconds for full ISO string
                                hours = currentTime.getHours();
                                minutes = currentTime.getMinutes();
                              }
                              
                              // Create new date with preserved time
                              const newDate = new Date(date);
                              newDate.setHours(hours, minutes, 0, 0);
                              
                              // Format as local datetime string
                              const year = newDate.getFullYear();
                              const month = (newDate.getMonth() + 1).toString().padStart(2, '0');
                              const day = newDate.getDate().toString().padStart(2, '0');
                              const hour = newDate.getHours().toString().padStart(2, '0');
                              const minute = newDate.getMinutes().toString().padStart(2, '0');
                              
                              setScheduledDateTime(`${year}-${month}-${day}T${hour}:${minute}`);
                            }}
                            className={`p-4 rounded-lg text-sm font-semibold transition-all shadow-sm hover:shadow-md ${
                              isSelected
                                ? 'bg-servify-green text-white shadow-lg border-2 border-servify-green'
                                : 'bg-white text-servify-dark-grey hover:bg-servify-green/10 border-2 border-servify-green/20 hover:border-servify-green/40'
                            }`}
                            data-testid={`button-date-${option.toLowerCase()}`}
                          >
                            <div className="flex flex-col items-center gap-1">
                              <span>{option}</span>
                              <span className="text-xs opacity-75">
                                {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    
                    {/* Custom Date Picker */}
                    <div className="relative">
                      <input
                        type="date"
                        min={new Date().toISOString().split('T')[0]}
                        value={scheduledDateTime ? scheduledDateTime.split('T')[0] : ''}
                        onChange={(e) => {
                          if (e.target.value) {
                            const selectedDate = new Date(e.target.value);
                            let hours = 9; // Default hour
                            let minutes = 0; // Default minutes
                            
                            // If user has already set a time, preserve it exactly
                            if (scheduledDateTime) {
                              const currentTime = new Date(scheduledDateTime + ':00'); // Add seconds for full ISO string
                              hours = currentTime.getHours();
                              minutes = currentTime.getMinutes();
                            }
                            
                            selectedDate.setHours(hours, minutes, 0, 0);
                            
                            // Format as local datetime string
                            const year = selectedDate.getFullYear();
                            const month = (selectedDate.getMonth() + 1).toString().padStart(2, '0');
                            const day = selectedDate.getDate().toString().padStart(2, '0');
                            const hour = selectedDate.getHours().toString().padStart(2, '0');
                            const minute = selectedDate.getMinutes().toString().padStart(2, '0');
                            
                            setScheduledDateTime(`${year}-${month}-${day}T${hour}:${minute}`);
                          }
                        }}
                        className="w-full bg-white border-2 border-servify-green/20 rounded-lg px-4 py-3 text-sm font-semibold text-servify-dark-grey focus:ring-2 focus:ring-servify-green focus:border-servify-green shadow-sm hover:border-servify-green/40 transition-colors"
                        data-testid="input-custom-date"
                      />
                      <label className="absolute -top-2 left-3 px-2 bg-white text-xs font-medium text-servify-medium-grey">
                        Or pick a custom date
                      </label>
                    </div>
                  </div>
                </div>

                {/* Timezone Selector */}
                <div className="bg-gradient-to-br from-servify-green/5 to-servify-purple/5 rounded-lg p-4 border border-servify-purple/10">
                  <Label className="text-sm font-medium text-servify-dark-grey mb-3 block flex items-center gap-2">
                    <Globe className="h-4 w-4 text-servify-purple" />
                    Select Timezone
                  </Label>
                  <select
                    value={selectedTimezone}
                    onChange={(e) => setSelectedTimezone(e.target.value)}
                    className="w-full bg-white border-2 border-servify-purple/20 rounded-lg px-4 py-3 text-sm font-semibold text-servify-dark-grey focus:ring-2 focus:ring-servify-purple focus:border-servify-purple shadow-sm hover:border-servify-purple/40 transition-colors"
                    data-testid="select-timezone"
                  >
                    <option value="Asia/Kolkata">Indian Standard Time (IST)</option>
                    <option value="UTC">Coordinated Universal Time (UTC)</option>
                    <option value="America/New_York">Eastern Time (ET)</option>
                    <option value="America/Chicago">Central Time (CT)</option>
                    <option value="America/Denver">Mountain Time (MT)</option>
                    <option value="America/Los_Angeles">Pacific Time (PT)</option>
                    <option value="Europe/London">British Time (GMT/BST)</option>
                    <option value="Europe/Berlin">Central European Time (CET)</option>
                    <option value="Australia/Sydney">Australian Eastern Time (AEST)</option>
                    <option value="Asia/Singapore">Singapore Time (SGT)</option>
                    <option value="Asia/Tokyo">Japan Standard Time (JST)</option>
                  </select>
                  <div className="text-center mt-2">
                    <span className="text-xs text-servify-medium-grey">Messages will be sent at the selected time in this timezone</span>
                  </div>
                </div>
              </div>

              {/* Message Preview and Actions */}
              <div className="space-y-4">
                {/* Message Preview */}
                <div className="bg-gradient-to-br from-servify-purple/5 to-servify-green/5 rounded-lg p-4 border border-servify-purple/10">
                  <Label className="text-sm font-medium text-servify-dark-grey mb-3 block flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    Message Preview
                  </Label>
                  <div className="bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
                    <p className="text-sm text-servify-medium-grey leading-relaxed">
                      {bulkMessageContent || "Enter your message content above"}
                    </p>
                  </div>
                </div>

                {/* Scheduled Time Display */}
                {scheduledDateTime && (
                  <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-600 font-medium">
                      Scheduled for: {new Date(scheduledDateTime).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setShowSchedulingModal(false)}
                className="flex-1 h-11 text-base rounded-lg border-2"
                data-testid="button-cancel-schedule"
              >
                Cancel
              </Button>
              <Button 
                onClick={() => handleBulkMessage(true)}
                disabled={!scheduledDateTime || !bulkMessageContent.trim() || bulkMessageMutation.isPending}
                className="flex-1 h-11 text-base bg-servify-purple hover:bg-servify-purple/90 rounded-lg shadow-lg"
                data-testid="button-confirm-schedule"
              >
                {bulkMessageMutation.isPending ? (
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                ) : (
                  <Calendar className="h-5 w-5 mr-2" />
                )}
                Schedule Message
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}